
import React, { useState, useEffect, useMemo } from 'react';
import { UserRole, RequestStatus, MeetingPlatform, ChatRequest, SubscriptionTier, StudentProfile, ProfessionalProfile, SchedulingStatus } from '../types.ts';
import { dbService } from '../services/dbService.ts';
import { aiService } from '../services/aiService.ts';

interface StudentDashboardProps {
  user: any;
  onLogout: () => void;
  onChangeTab: (tab: string) => void;
  onGoToChat: (id: string) => void;
  activeTab?: string;
}

const StudentDashboard: React.FC<StudentDashboardProps> = ({ 
  user, 
  onLogout, 
  onChangeTab, 
  onGoToChat,
  activeTab = 'connect' 
}) => {
  const [profile, setProfile] = useState<StudentProfile | undefined>(dbService.getStudentProfile(user.id));
  const [allProfs, setAllProfs] = useState<ProfessionalProfile[]>(dbService.getAllProfessionalProfiles());
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // View State
  const [targetType, setTargetType] = useState<'professionals' | 'students'>('professionals');
  const [subTab, setSubTab] = useState<'radar' | 'status'>('radar'); // Only for professionals view
  
  // Filtering Options
  const [prioritizeAlumni, setPrioritizeAlumni] = useState(false);

  // Selection State
  const [viewingProfDetail, setViewingProfDetail] = useState<ProfessionalProfile | null>(null);
  const [viewingStudentDetail, setViewingStudentDetail] = useState<StudentProfile | null>(null);
  
  const [myRequests, setMyRequests] = useState<ChatRequest[]>(dbService.getRequestsForUser(user.id, UserRole.STUDENT));

  // Student Peer Filter State (Input)
  const [tempFilterYear, setTempFilterYear] = useState('All');
  const [tempFilterMajor, setTempFilterMajor] = useState('');
  const [tempFilterExp, setTempFilterExp] = useState(false);

  // Student Peer Filter State (Applied)
  const [appliedFilters, setAppliedFilters] = useState({
    year: 'All',
    major: '',
    hasExp: false
  });

  const userTier = user.tier || SubscriptionTier.BASIC;
  const config = { limit: userTier === SubscriptionTier.ADVANCED ? 20 : userTier === SubscriptionTier.STANDARD ? 6 : 3, name: userTier };

  const currentMonthRequests = myRequests.filter(r => {
    const d = new Date(r.createdAt);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  
  const isAtQuota = currentMonthRequests >= config.limit;

  useEffect(() => {
    if (profile && targetType === 'professionals') fetchRecommendations();
    setMyRequests(dbService.getRequestsForUser(user.id, UserRole.STUDENT));
  }, [profile?.major, profile?.school, user.id, targetType, prioritizeAlumni]);

  const fetchRecommendations = async () => {
    setIsLoading(true);
    try {
      const recs = await aiService.getRecommendations(profile, allProfs, prioritizeAlumni);
      setRecommendations(recs);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const applyPeerFilters = () => {
    setAppliedFilters({
      year: tempFilterYear,
      major: tempFilterMajor,
      hasExp: tempFilterExp
    });
  };

  const mappedRecommendations = useMemo(() => {
    return recommendations.map(rec => {
      const prof = allProfs.find(p => p.userId === rec.professionalId);
      if (!prof) return null;
      return { ...rec, prof };
    }).filter(Boolean) as any[];
  }, [recommendations, allProfs]);

  // Peer Matching Logic
  const filteredPeers = useMemo(() => {
    if (!profile) return [];
    const allStudents = dbService.getAllStudentProfiles();
    
    return allStudents.filter(peer => {
      if (peer.userId === user.id) return false; // Exclude self
      if (peer.school !== profile.school) return false; // Strict Campus Scope
      
      const matchYear = appliedFilters.year === 'All' || peer.yearOfStudy === appliedFilters.year;
      const matchMajor = appliedFilters.major === '' || peer.major.toLowerCase().includes(appliedFilters.major.toLowerCase());
      const matchExp = !appliedFilters.hasExp || (peer.workHistory && peer.workHistory.length > 0);

      return matchYear && matchMajor && matchExp;
    }).sort((a, b) => {
        // Sort seniors/experienced first
        const aScore = (a.workHistory?.length || 0) * 10;
        const bScore = (b.workHistory?.length || 0) * 10;
        return bScore - aScore;
    });
  }, [profile, user.id, appliedFilters]);

  const getProf = (profId: string) => allProfs.find(p => p.userId === profId);
  const pendingRequests = myRequests.filter(r => r.status === RequestStatus.PENDING);
  const acceptedRequests = myRequests.filter(r => r.status === RequestStatus.ACCEPTED || r.status === RequestStatus.COMPLETED);

  const handleRequestChat = (recipient: ProfessionalProfile | StudentProfile, isStudentPeer: boolean = false) => {
    if (isAtQuota) {
      alert(`Monthly quota reached for ${config.name} tier.`);
      onChangeTab('membership');
      return;
    }
    
    // Find the recommendation data to attach rationale to the request
    const recData = !isStudentPeer 
      ? mappedRecommendations.find(m => m.prof.userId === (recipient as ProfessionalProfile).userId) 
      : null;

    // Create Request
    const newReq: ChatRequest = {
      id: `req_${Math.random().toString(36).substr(2, 9)}`,
      studentId: user.id,
      professionalId: isStudentPeer ? recipient.userId : (recipient as ProfessionalProfile).userId, 
      status: RequestStatus.PENDING,
      studentNote: isStudentPeer ? `Hi ${recipient.fullName}, I'm also at ${profile?.school}. Would love to hear about your experience.` : "Hi, I'd love to connect and learn more about your journey.",
      
      // PERSIST INTELLIGENCE
      synergyScore: isStudentPeer ? 90 : recData?.score || 85,
      matchRationale: isStudentPeer ? "Campus Peer Alignment" : recData?.rationale || "High potential based on industry alignment.",
      synergyThesis: isStudentPeer ? "Shared academic background." : recData?.synergyThesis || "Matches your target role criteria.",
      
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    dbService.saveRequest(newReq);
    setMyRequests(dbService.getRequestsForUser(user.id, UserRole.STUDENT));
    setViewingProfDetail(null);
    setViewingStudentDetail(null);
    onGoToChat(newReq.id);
  };

  return (
    <div className="p-4 md:p-12 lg:p-16 bg-[#F8FAFC] dark:bg-slate-950 min-h-screen transition-colors duration-500 relative overflow-x-hidden font-sans">
        <header className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 md:mb-12 gap-6 relative z-10">
          <div>
            <span className="text-[9px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-2 block">Network Discovery</span>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">Connect.</h1>
          </div>
          
          <div className="flex bg-white dark:bg-slate-900 p-1.5 rounded-2xl md:rounded-[1.8rem] border border-slate-200 dark:border-slate-800 shadow-xl w-full md:w-auto overflow-x-auto">
            <button 
              onClick={() => setTargetType('professionals')}
              className={`flex-1 md:flex-none px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-[1.4rem] text-[10px] md:text-[11px] font-black uppercase tracking-widest transition-all ${targetType === 'professionals' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
            >
              Professionals
            </button>
            <button 
              onClick={() => setTargetType('students')}
              className={`flex-1 md:flex-none px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-[1.4rem] text-[10px] md:text-[11px] font-black uppercase tracking-widest transition-all ${targetType === 'students' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
            >
              Campus Peers
            </button>
          </div>
        </header>

        {targetType === 'professionals' && (
          <>
            {/* Sub-Tabs for Professional View */}
            <div className="flex flex-col md:flex-row gap-4 mb-8 justify-between items-start md:items-center">
               <div className="flex gap-4">
                  <button onClick={() => setSubTab('radar')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border ${subTab === 'radar' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Discovery Radar</button>
                  <button onClick={() => setSubTab('status')} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border ${subTab === 'status' ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>Pipeline Status</button>
               </div>
               
               {/* Alumni Toggle */}
               <label className="flex items-center gap-3 cursor-pointer group bg-white dark:bg-slate-900 p-2 pr-4 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm">
                  <div className={`w-10 h-6 rounded-full p-1 transition-colors relative ${prioritizeAlumni ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}>
                     <div className={`w-4 h-4 bg-white rounded-full shadow-sm transform transition-transform ${prioritizeAlumni ? 'translate-x-4' : 'translate-x-0'}`}></div>
                  </div>
                  <input type="checkbox" className="hidden" checked={prioritizeAlumni} onChange={(e) => setPrioritizeAlumni(e.target.checked)} />
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 group-hover:text-indigo-600 transition-colors">Prioritize Alumni</span>
               </label>
            </div>

            {subTab === 'radar' ? (
              <div className="space-y-6 md:space-y-0">
                 {/* DESKTOP RADAR VIEW */}
                 <div className="hidden md:block relative h-[600px] w-full bg-white dark:bg-slate-900/40 rounded-[4rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden animate-in fade-in">
                    {isLoading ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center z-50">
                         <div className="w-20 h-20 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-6"></div>
                         <p className="text-xs font-black uppercase tracking-[0.3em] text-indigo-600 animate-pulse">Searching for Professionals...</p>
                      </div>
                    ) : (
                      <div className="relative w-full h-full flex items-center justify-center">
                         <div className="z-20 w-32 h-32 bg-slate-900 dark:bg-indigo-500 rounded-full flex flex-col items-center justify-center text-white shadow-2xl border-8 border-white dark:border-slate-900">
                           <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-1">YOU</p>
                           <span className="text-3xl font-black">{user.fullName[0]}</span>
                         </div>
                         {mappedRecommendations.map((m, i) => {
                            const radius = 280 - (m.score * 2.2);
                            const angle = (i * (360 / Math.max(mappedRecommendations.length, 1))) * (Math.PI / 180);
                            const x = Math.cos(angle) * radius;
                            const y = Math.sin(angle) * radius;
                            return (
                              <div key={m.prof.userId} className="absolute transition-all duration-1000 animate-in zoom-in group" style={{ transform: `translate(calc(50% + ${x}px), calc(50% + ${y}px))` }}>
                                <button onClick={() => setViewingProfDetail(m.prof)} className={`relative w-20 h-20 rounded-full border-4 flex items-center justify-center transition-all hover:scale-110 shadow-lg ${m.score >= 95 ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white'}`}>
                                  <span className="text-2xl font-black">{m.prof.fullName[0]}</span>
                                  {m.score >= 95 && <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-400 rounded-full border-2 border-white dark:border-slate-900 animate-pulse"></div>}
                                </button>
                              </div>
                            );
                         })}
                      </div>
                    )}
                 </div>

                 {/* MOBILE OPTIMIZED SYNERGY STACK */}
                 <div className="md:hidden space-y-4">
                    {isLoading ? (
                      <div className="h-96 flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800">
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-6"></div>
                        <div className="text-center text-indigo-400 font-black uppercase text-[10px] tracking-widest animate-pulse">Searching for Professionals...</div>
                      </div>
                    ) : mappedRecommendations.length === 0 ? (
                      <div className="p-8 bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-slate-200 text-center text-slate-400">Horizon Clear.</div>
                    ) : mappedRecommendations.map((m) => (
                      <div key={m.prof.userId} onClick={() => setViewingProfDetail(m.prof)} className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-5 active:scale-95 transition-transform">
                        <div className="relative shrink-0">
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-xl ${m.score >= 95 ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                            {m.prof.fullName[0]}
                          </div>
                          <div className="absolute -bottom-1 -right-1 bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded-lg border border-slate-100 dark:border-slate-800 text-[8px] font-black text-indigo-600">{m.score}%</div>
                        </div>
                        <div className="min-w-0">
                          <p className="font-black text-slate-900 dark:text-white truncate">{m.prof.fullName}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{m.prof.company}</p>
                        </div>
                        <i className="fas fa-chevron-right ml-auto text-slate-200"></i>
                      </div>
                    ))}
                 </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 animate-in slide-in-from-right-10 duration-500">
                 <section className="space-y-6">
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                      <div className="w-1.5 h-6 bg-amber-400 rounded-full"></div> Handshakes
                    </h3>
                    <div className="space-y-3">
                      {pendingRequests.length === 0 && (
                        <div className="p-10 bg-white dark:bg-slate-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800 text-center text-slate-300 font-bold text-xs uppercase">No active attempts.</div>
                      )}
                      {pendingRequests.map(req => {
                        const p = getProf(req.professionalId);
                        return (
                          <div key={req.id} onClick={() => onGoToChat(req.id)} className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between group active:scale-95 transition-transform hover:shadow-md">
                            <div className="flex items-center gap-4">
                               <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 text-slate-400 rounded-xl flex items-center justify-center font-black text-sm">{p?.fullName[0]}</div>
                               <div>
                                  <h4 className="font-black text-slate-900 dark:text-white text-sm">{p?.fullName}</h4>
                                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">In Review</p>
                               </div>
                            </div>
                            <i className="fas fa-chevron-right text-slate-200"></i>
                          </div>
                        );
                      })}
                    </div>
                 </section>

                 <section className="space-y-6">
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                       <div className="w-1.5 h-6 bg-emerald-500 rounded-full"></div> Live Cycles
                    </h3>
                    <div className="space-y-3">
                      {acceptedRequests.length === 0 && (
                        <div className="p-10 bg-white dark:bg-slate-900 rounded-[2rem] border border-dashed border-slate-200 dark:border-slate-800 text-center text-slate-300 font-bold text-xs uppercase">Queue empty.</div>
                      )}
                      {acceptedRequests.map(req => {
                        const p = getProf(req.professionalId);
                        return (
                          <div key={req.id} onClick={() => onGoToChat(req.id)} className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between active:scale-95 transition-transform hover:shadow-md">
                            <div className="flex items-center gap-4">
                               <div className="relative">
                                  <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-xl flex items-center justify-center font-black text-sm">{p?.fullName[0]}</div>
                                  {req.schedulingStatus === SchedulingStatus.CONFIRMED && <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white"></div>}
                               </div>
                               <div>
                                  <p className="font-black text-slate-900 dark:text-white text-sm">{p?.fullName}</p>
                                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{req.schedulingStatus === SchedulingStatus.CONFIRMED ? 'Active Hub' : 'Awaiting Schedule'}</p>
                               </div>
                            </div>
                            <i className="fas fa-chevron-right text-slate-200"></i>
                          </div>
                        );
                      })}
                    </div>
                 </section>
              </div>
            )}
          </>
        )}

        {/* Detailed Dossier - Immersive Bottom Sheet for Mobile (PROFESSIONAL) */}
        {viewingProfDetail && (
           <div className="fixed inset-0 z-[1001] bg-slate-900/50 dark:bg-black/95 backdrop-blur-md flex items-end md:items-start justify-center p-0 md:p-6 lg:p-12 overflow-y-auto animate-in fade-in duration-500">
             <div className="bg-white dark:bg-slate-950 w-full max-w-5xl rounded-t-[3rem] md:rounded-[5rem] shadow-2xl p-8 md:p-20 relative flex flex-col gap-8 md:gap-12 animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-500 max-h-[95vh] md:max-h-none overflow-y-auto scrollbar-hide md:my-10 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => setViewingProfDetail(null)} className="sticky top-0 ml-auto md:absolute md:top-12 md:right-12 w-12 h-12 bg-slate-50 dark:bg-slate-900 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-full flex items-center justify-center transition-all z-50"><i className="fas fa-times"></i></button>
                
                <header className="flex flex-col md:flex-row gap-6 md:gap-12 items-center md:items-start text-center md:text-left">
                   <div className="w-24 h-24 md:w-32 md:h-32 bg-indigo-600 text-white rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center text-4xl md:text-5xl font-black shadow-xl shrink-0">{viewingProfDetail.fullName.charAt(0)}</div>
                   <div className="flex-1">
                      <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                         <span className="text-[8px] font-black text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-indigo-100 dark:border-indigo-900/50">Verified Pro</span>
                         <span className="text-[8px] font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-100 dark:border-emerald-900/50">Neural Match: {mappedRecommendations.find(m => m.prof.userId === viewingProfDetail.userId)?.score}%</span>
                         
                         {/* Location Match Badge */}
                         {profile?.networkingCity && viewingProfDetail.city.toLowerCase().includes(profile.networkingCity.toLowerCase()) && (
                            <span className="text-[8px] font-black text-white bg-indigo-600 px-3 py-1 rounded-full uppercase tracking-widest border border-indigo-500 shadow-lg shadow-indigo-500/30 animate-pulse">
                               <i className="fas fa-map-marker-alt mr-1"></i> Target City Match
                            </span>
                         )}
                      </div>
                      <h3 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none mb-3">{viewingProfDetail.fullName}</h3>
                      <p className="text-xl md:text-2xl font-bold text-slate-400 tracking-tight">
                        {viewingProfDetail.title} <span className="text-slate-300 mx-1">@</span> {viewingProfDetail.company}
                      </p>
                      <p className="text-sm font-bold text-slate-400 mt-2 uppercase tracking-widest flex items-center justify-center md:justify-start gap-2">
                         <i className="fas fa-location-dot"></i> {viewingProfDetail.city}
                      </p>
                   </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                   <div className="space-y-6">
                      <section className="bg-blue-50 dark:bg-indigo-900/10 p-8 rounded-[2.5rem] border border-blue-100 dark:border-indigo-900/30">
                         <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Professional Trajectory</h4>
                         <p className="text-lg md:text-xl font-bold text-slate-900 dark:text-white leading-relaxed">
                           "{viewingProfDetail.bio}"
                         </p>
                      </section>

                      {/* NEW: Comprehensive Education & Personal Details */}
                      <section className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
                         <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2"><i className="fas fa-book-open"></i> Alma Mater & Origins</h4>
                         <div className="space-y-4">
                            <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800 pb-3">
                               <span className="text-xs font-black text-slate-500">Grad School</span>
                               <span className="text-sm font-bold text-slate-900 dark:text-white">{viewingProfDetail.gradUniversity} <span className="text-slate-400 text-xs">'{viewingProfDetail.gradUniversityYear}</span></span>
                            </div>
                            <div className="flex justify-between items-center border-b border-slate-50 dark:border-slate-800 pb-3">
                               <span className="text-xs font-black text-slate-500">High School</span>
                               <span className="text-sm font-bold text-slate-900 dark:text-white">{viewingProfDetail.highSchool} <span className="text-slate-400 text-xs">'{viewingProfDetail.highSchoolYear}</span></span>
                            </div>
                            <div>
                               <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Personal Interests</span>
                               <div className="flex flex-wrap gap-2">
                                  {viewingProfDetail.hobbies?.map((hobby, i) => (
                                    <span key={i} className="px-3 py-1 bg-slate-50 dark:bg-slate-800 rounded-lg text-[10px] font-bold text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">{hobby}</span>
                                  ))}
                               </div>
                            </div>
                         </div>
                      </section>

                      <section className="bg-slate-50 dark:bg-slate-800/50 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800">
                         <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Past Experience</h4>
                         <div className="space-y-4">
                           {viewingProfDetail.workHistory?.map((exp, i) => (
                             <div key={i} className="flex gap-4 items-start">
                                <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full mt-2 shrink-0"></div>
                                <div>
                                   <p className="font-bold text-slate-900 dark:text-white text-sm">{exp.role}</p>
                                   <p className="text-[10px] font-black text-slate-400 uppercase">{exp.company}</p>
                                </div>
                             </div>
                           ))}
                         </div>
                      </section>
                   </div>

                   <div className="space-y-6">
                      <section className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm">
                         <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Mentorship DNA</h4>
                         <div className="flex flex-wrap gap-2">
                            {viewingProfDetail.topics.map((t, i) => (
                              <span key={i} className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl text-[10px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-900/50">{t}</span>
                            ))}
                         </div>
                      </section>

                      <div className="p-8 bg-blue-950 text-white rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
                         <div className="absolute top-0 right-0 p-8 opacity-10 text-9xl pointer-events-none group-hover:rotate-12 transition-transform duration-1000"><i className="fas fa-handshake"></i></div>
                         <div className="relative z-10">
                            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-300 mb-4">Sage Synergy Analysis</p>
                            <p className="text-xl font-bold leading-relaxed mb-6">
                               "{mappedRecommendations.find(m => m.prof.userId === viewingProfDetail.userId)?.rationale}"
                            </p>
                            
                            {/* New Current Role Value Insight */}
                            <div className="bg-white/10 p-4 rounded-xl border border-white/10 mb-8">
                                <p className="text-[9px] font-black uppercase tracking-widest text-indigo-300 mb-2">Tactical Value • {viewingProfDetail.title}</p>
                                <p className="text-sm font-medium text-blue-50 leading-relaxed">
                                    "{mappedRecommendations.find(m => m.prof.userId === viewingProfDetail.userId)?.currentRoleValue || "Analyzing current role advantage..."}"
                                </p>
                            </div>

                            <button onClick={() => handleRequestChat(viewingProfDetail)} className="w-full py-5 bg-white text-blue-950 rounded-[1.5rem] font-black text-xs uppercase tracking-widest hover:bg-blue-50 transition active:scale-95 shadow-xl">
                               Initiate Connection Protocol
                            </button>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
           </div>
        )}

        {targetType === 'students' && (
           <div className="space-y-12 animate-in slide-in-from-right-10 duration-500">
              {/* Filter Bar */}
              <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl flex flex-col md:flex-row gap-6 items-end">
                 <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Year of Study</label>
                       <select value={tempFilterYear} onChange={(e) => setTempFilterYear(e.target.value)} className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-sm text-slate-900 dark:text-white outline-none focus:ring-2 ring-indigo-500/20">
                          {['All', '1st Year', '2nd Year', '3rd Year', '4th Year', 'Graduate'].map(y => <option key={y} value={y}>{y}</option>)}
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Major Keyword</label>
                       <input 
                         type="text" 
                         placeholder="e.g. Computer Science"
                         value={tempFilterMajor}
                         onChange={(e) => setTempFilterMajor(e.target.value)}
                         className="w-full p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl border-none font-bold text-sm text-slate-900 dark:text-white outline-none focus:ring-2 ring-indigo-500/20"
                       />
                    </div>
                    <div className="flex items-center gap-4 h-full pt-6 px-2">
                       <label className="flex items-center gap-3 cursor-pointer group">
                          <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${tempFilterExp ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                             {tempFilterExp && <i className="fas fa-check text-white text-xs"></i>}
                          </div>
                          <input type="checkbox" className="hidden" checked={tempFilterExp} onChange={(e) => setTempFilterExp(e.target.checked)} />
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 group-hover:text-indigo-600 transition-colors">Has Internship Exp?</span>
                       </label>
                    </div>
                 </div>
                 <button onClick={applyPeerFilters} className="w-full md:w-auto px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-blue-950 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-transform shadow-xl">
                    Apply Filters
                 </button>
              </div>

              {/* Peer Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                 {filteredPeers.length === 0 ? (
                    <div className="col-span-full p-20 text-center opacity-40">
                       <i className="fas fa-user-friends text-6xl text-slate-300 mb-6"></i>
                       <p className="font-black uppercase tracking-widest text-sm text-slate-400">No peers found matching criteria.</p>
                    </div>
                 ) : filteredPeers.map(peer => (
                   <div key={peer.userId} onClick={() => setViewingStudentDetail(peer)} className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:border-indigo-500/20 hover:shadow-2xl transition-all cursor-pointer group">
                      <div className="flex items-start justify-between mb-6">
                         <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-2xl font-black text-slate-400 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-900/20 group-hover:text-emerald-600 transition-colors">
                            {peer.fullName[0]}
                         </div>
                         <div className="bg-slate-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-400">
                            {peer.yearOfStudy}
                         </div>
                      </div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1">{peer.fullName}</h3>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-6 truncate">{peer.major}</p>
                      
                      {peer.workHistory && peer.workHistory.length > 0 ? (
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50 mb-6">
                           <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Experience</p>
                           <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{peer.workHistory[0].role}</p>
                           <p className="text-[10px] text-slate-500 truncate">{peer.workHistory[0].company}</p>
                        </div>
                      ) : (
                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-700/50 mb-6 opacity-50">
                           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Seeking Internships</p>
                        </div>
                      )}

                      <button className="w-full py-3 bg-emerald-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-emerald-500 transition shadow-lg">View Profile</button>
                   </div>
                 ))}
              </div>
           </div>
        )}

        {/* Peer Detail Modal */}
        {viewingStudentDetail && (
           <div className="fixed inset-0 z-[1001] bg-slate-900/50 dark:bg-black/95 backdrop-blur-xl flex items-end md:items-start justify-center p-0 md:p-6 lg:p-12 overflow-y-auto animate-in fade-in duration-500">
             <div className="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-t-[3rem] md:rounded-[4rem] shadow-4xl p-8 md:p-16 relative flex flex-col gap-8 md:gap-10 animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-500 max-h-[95vh] md:max-h-none overflow-y-auto scrollbar-hide md:my-10 border-t border-slate-100 dark:border-slate-800">
                <button onClick={() => setViewingStudentDetail(null)} className="sticky top-0 ml-auto md:absolute md:top-12 md:right-12 w-12 h-12 bg-slate-50 dark:bg-slate-900 text-slate-400 hover:text-slate-900 rounded-full flex items-center justify-center transition-all z-50"><i className="fas fa-times"></i></button>
                
                <header className="flex flex-col md:flex-row gap-6 md:gap-10 items-center md:items-start text-center md:text-left">
                   <div className="w-24 h-24 md:w-32 md:h-32 bg-emerald-600 text-white rounded-[2rem] md:rounded-[2.5rem] flex items-center justify-center text-4xl md:text-5xl font-black shadow-2xl shrink-0">{viewingStudentDetail.fullName.charAt(0)}</div>
                   <div className="flex-1">
                      <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                         <span className="text-[8px] font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-100 dark:border-emerald-900/50">Verified Student</span>
                         <span className="text-[8px] font-black text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full uppercase tracking-widest border border-blue-100 dark:border-blue-900/50">{viewingStudentDetail.school}</span>
                      </div>
                      <h3 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tighter leading-none mb-3">{viewingStudentDetail.fullName}</h3>
                      <p className="text-lg md:text-xl font-bold text-slate-400 tracking-tight">{viewingStudentDetail.major} <span className="text-slate-200 mx-1">•</span> {viewingStudentDetail.yearOfStudy}</p>
                   </div>
                </header>

                <section className="bg-slate-50 dark:bg-slate-800/50 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800">
                   <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">About & Goals</h4>
                   <p className="text-base font-medium text-slate-600 dark:text-slate-300 leading-relaxed mb-6">"{viewingStudentDetail.bio || 'No bio yet.'}"</p>
                   <div className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                      <p className="text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-1">Aspiration</p>
                      <p className="text-sm font-bold text-slate-900 dark:text-white">"{viewingStudentDetail.goals}"</p>
                   </div>
                </section>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <div className="space-y-4">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Experience</h4>
                      {viewingStudentDetail.workHistory && viewingStudentDetail.workHistory.length > 0 ? (
                        viewingStudentDetail.workHistory.map((exp, i) => (
                           <div key={i} className="p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                              <p className="font-bold text-slate-900 dark:text-white text-sm">{exp.role}</p>
                              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{exp.company}</p>
                           </div>
                        ))
                      ) : (
                        <div className="p-6 bg-slate-50 dark:bg-slate-800 rounded-3xl border border-dashed border-slate-200 text-center text-xs font-bold text-slate-400">No work history listed.</div>
                      )}
                   </div>
                   <div className="space-y-4">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-2">Interests</h4>
                      <div className="flex flex-wrap gap-2">
                         {viewingStudentDetail.interests?.map((tag, i) => (
                           <span key={i} className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300 rounded-xl text-[9px] font-black uppercase tracking-widest">{tag}</span>
                         ))}
                      </div>
                   </div>
                </div>

                <div className="mt-auto pt-8 border-t border-slate-100 dark:border-slate-800 flex gap-4 pb-8 md:pb-0">
                   <button onClick={() => setViewingStudentDetail(null)} className="px-8 py-5 text-slate-400 font-black uppercase text-xs tracking-widest hover:text-blue-950">Dismiss</button>
                   <button onClick={() => handleRequestChat(viewingStudentDetail, true)} className="flex-1 py-5 bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-emerald-500 transition active:scale-95">Message Peer</button>
                </div>
             </div>
           </div>
        )}
    </div>
  );
};

export default StudentDashboard;
